import { ACCOUNT_STATUS } from '@domain/constants/app-variables';
import {
	Owner,
	User,
	IS_EMPTY,
	PROJECT_STAGE_UUID_MAP,
	USERS_LIST_FOR_POPOVER,
} from 'cargo-chief-commons';
import { INPUT_TYPES } from '../../../shared/tabbed-table/constants';
import { AccountSettingsService } from '@service/accountSettings/account-settings.service';

export const PROJECT_FIELDS = {
	START_DATE: 'Start Date',
	ORIGIN_CITY: 'Origin City',
	ORIGIN_STATE: 'Origin State',
	DESTINATION_CITY: 'Destination City',
	DESTINATION_STATE: 'Destination State',
	CUSTOMER: 'Customer',
	VOLUME: 'Volume',
	EQUIPMENT: 'Equipment',
	COMMODITY: 'Commodity',
	WEIGHT: 'Weight',
	MAX_BUY: 'Max Buy',
	TARGET_CARRIER_RATE: 'Target Carrier Rate',
	PROCURED_CARRIERS: 'Procured Carriers',
	ASSIGNED_REPS: 'Assigned Reps',
	ORIGIN: 'Origin',
	DESTINATION: 'Destination',
	STAGE: 'Stage',
	TYPE: 'Type',
	ARCHIVED: 'Archived',
	REVENUE_CODE: 'Revenue Code',
	LANE: 'Lane',
	LOADS: 'Loads',
	CARRIER: 'Carrier',
};

export function buildHeaders(accountSettings: AccountSettingsService) {
	let headers = [
		{ dataName: 'select', formattedName: 'select' },
		{ dataName: 'startDate', formattedName: 'Start Date' },
		{ dataName: 'lanePath', formattedName: PROJECT_FIELDS.LANE },
		{ dataName: 'revenueCode', formattedName: PROJECT_FIELDS.REVENUE_CODE },
		{ dataName: 'monthlyVolumeDisplay', formattedName: PROJECT_FIELDS.VOLUME },
		{ dataName: 'commodity', formattedName: PROJECT_FIELDS.COMMODITY },
		{ dataName: 'maxBuy', formattedName: PROJECT_FIELDS.MAX_BUY },
		{ dataName: 'targetCarrierRate', formattedName: PROJECT_FIELDS.TARGET_CARRIER_RATE },
		{ dataName: 'procuredCarriers', formattedName: PROJECT_FIELDS.PROCURED_CARRIERS },
		{ dataName: 'loadsCount', formattedName: PROJECT_FIELDS.LOADS },
		{ dataName: 'assignedReps', formattedName: PROJECT_FIELDS.ASSIGNED_REPS },
		{ dataName: 'archived', formattedName: PROJECT_FIELDS.ARCHIVED },
	];
	if (accountSettings.PROJECT_GROUPS_ENABLED) {
		headers.push({ dataName: 'type', formattedName: PROJECT_FIELDS.TYPE });
	}
	if (accountSettings.ROLE_BASED_CUSTOMER_DATA_ACCESS) {
		headers.splice(5, 0, { dataName: 'customerName', formattedName: PROJECT_FIELDS.CUSTOMER });
	}
	return headers;
}

export function buildHeaderDataTypes(accountSettings: AccountSettingsService) {
	let types = [
		'string',
		'string',
		'string',
		'string',
		'string',
		'currency',
		'currency',
		'number',
		'number',
		'string',
		'string',
	];
	if (accountSettings.PROJECT_GROUPS_ENABLED) {
		types.push('string');
	}
	if (accountSettings.ROLE_BASED_CUSTOMER_DATA_ACCESS) {
		types.splice(5, 0, 'string');
	}
	return types;
}

export function buildDisplayedColumns(accountSettings: AccountSettingsService) {
	let columns = [
		'select',
		'startDate',
		'lanePath',
		'revenueCode',
		'monthlyVolumeDisplay',
		'commodity',
		'maxBuy',
		'targetCarrierRate',
		'procuredCarriers',
		'loadsCount',
		'assignedReps',
	];
	if (accountSettings.PROJECT_GROUPS_ENABLED) {
		columns.push('type');
	}
	if (accountSettings.ROLE_BASED_CUSTOMER_DATA_ACCESS) {
		columns.splice(5, 0, 'customerName');
	}
	return columns;
}

export function buildSortFields(accountSettings: AccountSettingsService) {
	let sortFields = [
		PROJECT_FIELDS.START_DATE,
		PROJECT_FIELDS.ORIGIN_CITY,
		PROJECT_FIELDS.ORIGIN_STATE,
		PROJECT_FIELDS.DESTINATION_CITY,
		PROJECT_FIELDS.DESTINATION_STATE,
		PROJECT_FIELDS.VOLUME,
		PROJECT_FIELDS.EQUIPMENT,
		PROJECT_FIELDS.COMMODITY,
		PROJECT_FIELDS.MAX_BUY,
		PROJECT_FIELDS.TARGET_CARRIER_RATE,
		PROJECT_FIELDS.PROCURED_CARRIERS,
		PROJECT_FIELDS.ASSIGNED_REPS,
		PROJECT_FIELDS.STAGE,
	];
	if (accountSettings.PROJECT_GROUPS_ENABLED) {
		sortFields.push(PROJECT_FIELDS.TYPE);
	}
	if (accountSettings.ROLE_BASED_CUSTOMER_DATA_ACCESS) {
		sortFields.splice(5, 0, PROJECT_FIELDS.CUSTOMER);
	}
	return sortFields;
}

export function buildFilerFields(accountSettings: AccountSettingsService) {
	let filters = [
		PROJECT_FIELDS.START_DATE,
		PROJECT_FIELDS.ORIGIN_CITY,
		PROJECT_FIELDS.ORIGIN_STATE,
		PROJECT_FIELDS.DESTINATION_CITY,
		PROJECT_FIELDS.DESTINATION_STATE,
		PROJECT_FIELDS.VOLUME,
		PROJECT_FIELDS.EQUIPMENT,
		PROJECT_FIELDS.COMMODITY,
		PROJECT_FIELDS.MAX_BUY,
		PROJECT_FIELDS.TARGET_CARRIER_RATE,
		PROJECT_FIELDS.PROCURED_CARRIERS,
		PROJECT_FIELDS.ASSIGNED_REPS,
		PROJECT_FIELDS.STAGE,
		PROJECT_FIELDS.ARCHIVED,
	];
	if (accountSettings.PROJECT_GROUPS_ENABLED) {
		filters.push(PROJECT_FIELDS.TYPE);
	}
	if (accountSettings.ROLE_BASED_CUSTOMER_DATA_ACCESS) {
		filters.splice(5, 0, PROJECT_FIELDS.CUSTOMER);
	}
	return filters;
}

export const SORT_FIELD_MAPS = {
	[PROJECT_FIELDS.START_DATE]: 'dueDate',
	[PROJECT_FIELDS.ORIGIN_CITY]: 'originCity',
	[PROJECT_FIELDS.ORIGIN_STATE]: 'originState',
	[PROJECT_FIELDS.DESTINATION_CITY]: 'destCity',
	[PROJECT_FIELDS.DESTINATION_STATE]: 'destState',
	[PROJECT_FIELDS.CUSTOMER]: 'customerName',
	[PROJECT_FIELDS.VOLUME]: 'volume',
	[PROJECT_FIELDS.EQUIPMENT]: 'equipmentType',
	[PROJECT_FIELDS.COMMODITY]: 'commodity',
	[PROJECT_FIELDS.MAX_BUY]: 'maxBuy',
	[PROJECT_FIELDS.TARGET_CARRIER_RATE]: 'bestRate',
	[PROJECT_FIELDS.PROCURED_CARRIERS]: 'carrierCount',
	[PROJECT_FIELDS.ASSIGNED_REPS]: 'assigneeIds',
	[PROJECT_FIELDS.STAGE]: 'stageIds',
	[PROJECT_FIELDS.TYPE]: 'type',
};

export let filterFieldsInputTypeMap: Map<string, string> = new Map<string, string>();

filterFieldsInputTypeMap.set(PROJECT_FIELDS.START_DATE, INPUT_TYPES.DATE);
filterFieldsInputTypeMap.set(PROJECT_FIELDS.ORIGIN_CITY, INPUT_TYPES.LOCATION);
filterFieldsInputTypeMap.set(PROJECT_FIELDS.ORIGIN_STATE, INPUT_TYPES.STATE);
filterFieldsInputTypeMap.set(PROJECT_FIELDS.DESTINATION_CITY, INPUT_TYPES.LOCATION);
filterFieldsInputTypeMap.set(PROJECT_FIELDS.DESTINATION_STATE, INPUT_TYPES.STATE);

filterFieldsInputTypeMap.set(PROJECT_FIELDS.CUSTOMER, INPUT_TYPES.CUSTOMER);
filterFieldsInputTypeMap.set(PROJECT_FIELDS.VOLUME, INPUT_TYPES.VOLUME);
filterFieldsInputTypeMap.set(PROJECT_FIELDS.EQUIPMENT, INPUT_TYPES.TRAILER_TYPE);
filterFieldsInputTypeMap.set(PROJECT_FIELDS.COMMODITY, INPUT_TYPES.STRING);
filterFieldsInputTypeMap.set(PROJECT_FIELDS.MAX_BUY, INPUT_TYPES.NUMBER);
filterFieldsInputTypeMap.set(PROJECT_FIELDS.TARGET_CARRIER_RATE, INPUT_TYPES.NUMBER);
filterFieldsInputTypeMap.set(PROJECT_FIELDS.PROCURED_CARRIERS, INPUT_TYPES.NUMBER);
filterFieldsInputTypeMap.set(PROJECT_FIELDS.ASSIGNED_REPS, INPUT_TYPES.USERS_LIST);
filterFieldsInputTypeMap.set(PROJECT_FIELDS.STAGE, INPUT_TYPES.STAGE);
filterFieldsInputTypeMap.set(PROJECT_FIELDS.ARCHIVED, INPUT_TYPES.BOOLEAN);
filterFieldsInputTypeMap.set(PROJECT_FIELDS.TYPE, INPUT_TYPES.PROJECT_TYPE);

export const ACTIVE_USERS = (users: User[]): Array<Owner> => {
	let result: Owner[] = [];
	result = USERS_LIST_FOR_POPOVER({
		users: users,
		assigneeId: null,
		removeHavingStatus: ACCOUNT_STATUS.INACTIVE,
	});
	return result;
};

export const PROJECT_STAGES = [
	{
		label: 'Awarded',
		type: PROJECT_STAGE_UUID_MAP.AWARDED,
	},
	{
		label: 'On Hold',
		type: PROJECT_STAGE_UUID_MAP.ON_HOLD,
	},
	{
		label: 'Re Bid',
		type: PROJECT_STAGE_UUID_MAP.RE_BID,
	},
	{
		label: 'New',
		type: PROJECT_STAGE_UUID_MAP.NEW,
	},
	{
		label: 'Live',
		type: PROJECT_STAGE_UUID_MAP.LIVE,
	},
	{
		label: 'Submitted',
		type: PROJECT_STAGE_UUID_MAP.SUBMITTED,
	},
];
